// src/components/PatientFormModal.tsx

import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, ModalContent, CloseButton, Button, Input } from '../styles/GlobalStyles';
import { addPatient, getPatientByName } from '../services/firebase';
import { Patient } from '../types';
import { useTranslation } from '../i18n';

interface PatientFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPatientAdded: () => void;
}

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 14px;
  margin-top: 8px;
`;

const SuccessMessage = styled.div`
  color: #166534;
  font-size: 14px;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  line-height: 1.4;
`;

const InstitutionInfo = styled.div`
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
`;

const PatientFormModal: React.FC<PatientFormModalProps> = ({ isOpen, onClose, onPatientAdded }) => {
  const { t } = useTranslation();
  const institutionNumber = sessionStorage.getItem('institutionNumber') || '';

  const [name, setName] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setName('');
      setErrors({});
      setSuccess('');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim().toLowerCase();
    if (!trimmedName) {
      setErrors({ name: t('patientForm.errors.nameRequired') });
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setSuccess('');

    try {
      const existing = await getPatientByName(institutionNumber, trimmedName);
      if (existing) {
        setErrors({ name: t('patientForm.errors.alreadyExists') });
        setIsSubmitting(false);
        return;
      }

      const patientData: Omit<Patient, 'id'> = {
        institutionNumber,
        name: trimmedName
      };
      await addPatient(patientData);
      setSuccess(t('patientForm.success_add', { name: trimmedName }));
      onPatientAdded();
      setTimeout(() => handleClose(), 1500);
    } catch (error) {
      console.error('환자 추가 오류:', error);
      setErrors({ general: t('patientForm.errors.addFailed') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setErrors({});
    setSuccess('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal onClick={handleClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>×</CloseButton>
        <h2 style={{ marginBottom: '24px', fontSize: '20px', fontWeight: '600' }}>
          {t('patientForm.modalTitle_add')}
        </h2>

        <InstitutionInfo>
          <div style={{ fontSize: '14px', color: '#0369a1', fontWeight: '500' }}>
            {t('patientForm.institutionInfo', { number: institutionNumber })}
          </div>
        </InstitutionInfo>

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <label htmlFor="patientName" style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#374151', fontSize: '14px' }}>
              {t('patientForm.fields.name')}
            </label>
            <Input
              id="patientName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase())}
              placeholder={t('patientForm.placeholders.name')}
              disabled={isSubmitting}
              autoFocus
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            <HelpText>{t('patientForm.helpText')}</HelpText>
          </FormGroup>

          {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <ButtonGroup>
            <Button type="button" onClick={handleClose} disabled={isSubmitting}>{t('common.cancel')}</Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? t('common.saving') : t('common.save')}
            </Button>
          </ButtonGroup>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default PatientFormModal;
